import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Phone,
  Sparkles,
  Quote,
  Star,
  CheckCircle2,
} from "lucide-react";
import { FloatingShapes } from "@/components/Decor";
import { SERVICES, VALEURS, CONTACT, ECOLES, HORAIRES_SOUTIEN, TEMOIGNAGES, formatXPF } from "@/lib/data";

const HERO_IMG =
  "https://images.pexels.com/photos/33980509/pexels-photo-33980509.jpeg";
const KIDS_IMG = "/hero-anuhei.png";
const WRITING_IMG =
  "https://images.pexels.com/photos/13779221/pexels-photo-13779221.jpeg";

export default function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative pt-28 sm:pt-32 pb-20 sm:pb-28 overflow-hidden">
        <FloatingShapes />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-ocean/10 text-xs uppercase tracking-[0.2em] text-coral font-semibold mb-6">
                <Sparkles size={12} />
                Papara & Mataiea — De la maternelle au collège
              </div>
              <h1 className="font-display font-semibold text-ocean text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight">
                Là où vos enfants
                <br />
                <span className="relative inline-block">
                  <span className="gradient-coral">apprennent autrement</span>
                  <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 300 14" fill="none">
                    <path d="M2 9 C 60 1, 160 17, 298 5" stroke="#DE6C5D" strokeWidth="3" strokeLinecap="round" fill="none" />
                  </svg>
                </span>
              </h1>
              <p className="mt-7 text-lg text-ocean/65 max-w-xl leading-relaxed">
                Un centre de soutien scolaire bienveillant à Papara : nous récupérons votre enfant à l'école,
                l'accompagnons dans ses devoirs, lui offrons un goûter et lui proposons des activités enrichissantes.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/inscription" className="btn-pill btn-accent group" data-testid="hero-cta-inscription">
                  Inscrire mon enfant
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href={`tel:${CONTACT.phoneRaw}`} className="btn-pill btn-ghost" data-testid="hero-cta-phone">
                  <Phone size={16} />
                  {CONTACT.phone}
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-ocean/70">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {["#DE6C5D","#0B2742","#8FB8CA"].map((c, i) => (
                      <div key={i} className="w-8 h-8 rounded-full border-2 border-sand flex items-center justify-center text-white text-[11px] font-semibold" style={{background: c}}>
                        {["A","M","T"][i]}
                      </div>
                    ))}
                  </div>
                  <span><strong className="text-ocean">+40 familles</strong> nous font confiance</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-coral text-coral" />)}
                  <span className="ml-1 text-ocean/70">Avis parents</span>
                </div>
              </div>
            </motion.div>

            {/* Hero image side */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative h-[480px] sm:h-[540px]">
                {/* Main image card */}
                <div
                  className="absolute top-0 right-0 w-[88%] h-[78%] rounded-[28px] overflow-hidden card-3d"
                  style={{
                    boxShadow: "0 50px 80px -30px rgba(11,39,66,0.35)",
                  }}
                >
                  <img src={KIDS_IMG} alt="Enfants en classe" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ocean/20 via-transparent to-transparent" />
                </div>

                {/* Floating stat card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="absolute bottom-2 left-0 glass rounded-2xl p-4 w-[220px] shadow-xl"
                >
                  <div className="text-xs uppercase tracking-widest text-coral font-semibold mb-1">Taux de satisfaction</div>
                  <div className="font-display text-3xl text-ocean font-semibold">98%</div>
                  <div className="text-xs text-ocean/60 mt-1">Familles satisfaites</div>
                </motion.div>

                {/* Floating small card */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                  className="absolute top-6 -left-2 sm:-left-6 glass rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3"
                >
                  <CheckCircle2 className="text-coral" size={22} />
                  <div>
                    <div className="text-xs text-ocean/60">Goûter & transport</div>
                    <div className="text-sm font-semibold text-ocean">Inclus</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE écoles */}
      <section className="relative py-8 border-y border-ocean/5 bg-white/40 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...ECOLES.papara, ...ECOLES.mataiea, ...ECOLES.papara, ...ECOLES.mataiea].map((e, i) => (
            <span key={i} className="mx-8 text-sm font-display text-ocean/40">
              {e} •
            </span>
          ))}
        </div>
      </section>

      {/* MOT DU DIRECTEUR — vidéo */}
      <section className="relative py-24 bg-gradient-to-b from-transparent to-coral/5">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* Text side */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="text-xs uppercase tracking-[0.25em] text-coral font-semibold mb-4 flex items-center gap-2">
                <Quote size={14} /> Le mot du directeur
              </div>
              <h2 className="font-display text-3xl sm:text-4xl text-ocean leading-tight mb-5">
                Un mot de bienvenue,<br />
                <em className="italic text-coral">en direct du centre</em>
              </h2>
              <p className="text-ocean/70 leading-relaxed mb-6">
                Découvrez la vision et l'engagement de <strong className="text-ocean">M. Vaianu Tupai</strong>,
                directeur du Centre de Soutien Scolaire Papara, envers vos enfants et leur réussite.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-ocean to-ocean-500 text-white flex items-center justify-center font-display font-semibold text-lg shrink-0">V</div>
                <div>
                  <div className="font-medium text-ocean">M. Vaianu Tupai</div>
                  <div className="text-xs text-ocean/55">Directeur</div>
                </div>
              </div>
            </div>

            {/* Video side */}
            <div className="lg:col-span-7 order-1 lg:order-2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(11,39,66,0.35)] bg-ocean" data-testid="director-video-wrapper">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster="/directeur-poster.jpg"
                  className="w-full h-auto block"
                  data-testid="director-video"
                >
                  <source src="/directeur.mp4" type="video/mp4" />
                  Votre navigateur ne prend pas en charge la lecture vidéo.
                </video>
              </div>
              {/* Decorative accents */}
              <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-3xl bg-coral/25 -z-10 animate-float-medium" />
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-lagoon/30 -z-10 animate-float-slow" />
            </div>
          </div>
        </div>
      </section>

      {/* VALEURS - Bento style */}
      <section className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.25em] text-coral font-semibold mb-4">Pourquoi nous choisir</div>
              <h2 className="font-display text-4xl sm:text-5xl text-ocean leading-tight">
                Un environnement <em className="font-display italic text-coral">pensé pour leur réussite</em>
              </h2>
            </div>
            <p className="lg:col-span-5 text-ocean/65 text-base leading-relaxed">
              Plus qu'un soutien scolaire, un véritable second foyer après l'école : encadrement professionnel,
              activités enrichissantes, et une attention particulière à l'épanouissement de chaque enfant.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALEURS.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="card-3d relative bg-white rounded-3xl p-6 border border-ocean/5"
                  data-testid={`valeur-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-2xl bg-coral/10 text-coral flex items-center justify-center mb-5">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-xl text-ocean mb-2">{v.title}</h3>
                  <p className="text-sm text-ocean/60 leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES bento */}
      <section className="relative py-16 sm:py-24 bg-gradient-to-b from-transparent via-lagoon/5 to-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-coral font-semibold mb-4">Nos services</div>
            <h2 className="font-display text-4xl sm:text-5xl text-ocean">
              Bien plus que du soutien scolaire
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
            {/* Large card */}
            <div className="md:col-span-4 md:row-span-3 relative rounded-3xl overflow-hidden min-h-[380px] card-3d" data-testid="service-card-soutien">
              <img src={WRITING_IMG} alt="Soutien scolaire" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ocean/95 via-ocean/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 text-white">
                <div className="text-xs uppercase tracking-[0.25em] text-coral mb-3">Service phare</div>
                <h3 className="font-display text-3xl sm:text-4xl mb-3">Soutien scolaire quotidien</h3>
                <p className="text-white/85 max-w-md mb-5">
                  De la sortie d'école à 17h. Devoirs encadrés, révisions, goûter et activités dans un cadre sécurisé.
                </p>
                <Link to="/services" className="inline-flex items-center gap-2 self-start px-5 py-2.5 rounded-full bg-white text-ocean font-medium text-sm hover:bg-coral hover:text-white transition-colors">
                  Découvrir <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {SERVICES.slice(1).map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.id}
                  className={`md:col-span-2 bg-white rounded-3xl p-6 border border-ocean/5 card-3d`}
                  data-testid={`service-card-${s.id}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-ocean/5 text-ocean flex items-center justify-center mb-4">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-lg text-ocean mb-1.5">{s.title}</h3>
                  <p className="text-sm text-ocean/60 leading-relaxed">{s.short}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HORAIRES + TARIFS preview */}
      <section className="relative py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div className="bg-ocean text-white rounded-3xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-coral/30 blur-3xl" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.25em] text-coral mb-3">Horaires soutien scolaire</div>
              <h3 className="font-display text-3xl mb-6">Du lundi au vendredi</h3>
              <div className="space-y-3">
                {HORAIRES_SOUTIEN.map((h) => (
                  <div key={h.label} className="flex justify-between border-b border-white/10 pb-3">
                    <span className="text-white/70 text-sm">{h.label}</span>
                    <span className="font-medium">{h.value}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-white/60">Hors jours fériés et journées pédagogiques.</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 sm:p-10 border border-ocean/5 relative overflow-hidden">
            <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-lagoon/20 blur-3xl" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.25em] text-coral mb-3">Tarifs soutien (mensuel)</div>
              <h3 className="font-display text-3xl text-ocean mb-6">Forfaits dégressifs</h3>
              <div className="space-y-2">
                {[{f:"2x / semaine", p:20000, s:3333},{f:"3x / semaine", p:27000, s:3000},{f:"4x / semaine", p:33000, s:2750},{f:"5x / semaine", p:38000, s:2533}].map((row) => (
                  <div key={row.f} className="flex items-center justify-between p-3 rounded-xl hover:bg-sand transition-colors">
                    <span className="text-ocean/75 text-sm">{row.f}</span>
                    <div className="text-right">
                      <span className="font-display text-xl text-ocean">{formatXPF(row.p)}</span>
                      <div className="text-[10px] text-ocean/45 mt-0.5">soit {formatXPF(row.s)} / demi-jour</div>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/tarifs" className="mt-6 inline-flex items-center gap-2 text-coral font-medium text-sm hover:gap-3 transition-all" data-testid="link-tarifs-full">
                Voir tous les tarifs <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES PARENTS */}
      <section className="relative py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-coral font-semibold mb-4">Ils nous font confiance</div>
            <h2 className="font-display text-4xl sm:text-5xl text-ocean leading-tight">
              La parole aux <em className="italic text-coral">parents</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TEMOIGNAGES.map((t, i) => (
              <motion.div
                key={t.prenom}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative bg-white rounded-3xl p-7 border border-ocean/5 card-3d"
                data-testid={`temoignage-${i}`}
              >
                {/* Big quote decoration */}
                <Quote className="absolute -top-3 -left-3 w-10 h-10 text-coral bg-sand p-2 rounded-full border border-coral/10" />
                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, s) => <Star key={s} size={14} className="fill-coral text-coral" />)}
                </div>
                {/* Text */}
                <p className="text-ocean/85 leading-relaxed mb-6 text-base">
                  « {t.texte} »
                </p>
                {/* Signature */}
                <div className="flex items-center gap-3 pt-4 border-t border-ocean/5">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-coral to-coral-500 text-white flex items-center justify-center font-display font-semibold text-lg shrink-0">
                    {t.prenom[0]}
                  </div>
                  <div>
                    <div className="font-medium text-ocean">{t.prenom}</div>
                    <div className="text-xs text-ocean/50">Parent — via Facebook</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[32px] overflow-hidden p-10 sm:p-16 text-center" style={{background: "linear-gradient(135deg, #0B2742 0%, #143A5E 60%, #DE6C5D 130%)"}}>
            <FloatingShapes className="opacity-30" />
            <div className="relative">
              <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight">
                Inscrivez votre enfant
                <br />
                <span className="text-coral">dès aujourd'hui</span>
              </h2>
              <p className="mt-5 text-white/75 max-w-xl mx-auto">
                Quelques minutes suffisent. Nous vous contactons sous 48h pour confirmer l'inscription
                et vous transmettre les coordonnées bancaires.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link to="/inscription" className="btn-pill btn-accent" data-testid="final-cta-inscription">
                  Démarrer l'inscription <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="btn-pill bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors">
                  Nous poser une question
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
